import { useEffect, useCallback } from 'react';
import { useAppStore } from '@/lib/store';

// ============================================================================
// PREMIUM AUDIO ENGINE SAMPLES
// ============================================================================
const ASSETS = {
    typewriter: {
        key: 'https://raw.githubusercontent.com/nathan-fiscaletti/keyboardsounds/master/keyboardsounds/profiles/typewriter/key.wav',
        space: 'https://raw.githubusercontent.com/nathan-fiscaletti/keyboardsounds/master/keyboardsounds/profiles/typewriter/space.wav',
        enter: 'https://raw.githubusercontent.com/nathan-fiscaletti/keyboardsounds/master/keyboardsounds/profiles/typewriter/enter.wav',
        delete: 'https://raw.githubusercontent.com/nathan-fiscaletti/keyboardsounds/master/keyboardsounds/profiles/typewriter/delete.wav',
        ding: 'https://raw.githubusercontent.com/orhun/daktilo/master/sounds/ding.mp3'
    },
    mechanical: {
        key: 'https://raw.githubusercontent.com/nathan-fiscaletti/keyboardsounds/master/keyboardsounds/profiles/alpaca/key1.mp3',
        space: 'https://raw.githubusercontent.com/nathan-fiscaletti/keyboardsounds/master/keyboardsounds/profiles/alpaca/space.mp3',
        enter: 'https://raw.githubusercontent.com/nathan-fiscaletti/keyboardsounds/master/keyboardsounds/profiles/alpaca/enter.mp3',
        delete: 'https://raw.githubusercontent.com/nathan-fiscaletti/keyboardsounds/master/keyboardsounds/profiles/alpaca/backspace.mp3'
    }
};

type SoundType = 'key' | 'space' | 'enter' | 'delete' | 'ding';

class AudioEngine {
    private context: AudioContext;
    private buffers: Map<string, AudioBuffer> = new Map();
    private loading: Map<string, Promise<AudioBuffer>> = new Map();

    constructor() {
        this.context = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    async loadSample(profile: 'mechanical' | 'typewriter', type: SoundType): Promise<AudioBuffer | null> {
        const url = ASSETS[profile][type as keyof typeof ASSETS['mechanical']];
        if (!url) return null;

        if (this.buffers.has(url)) return this.buffers.get(url)!;
        if (this.loading.has(url)) return this.loading.get(url)!;

        const promise = (async () => {
            try {
                const response = await fetch(url);
                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
                this.buffers.set(url, audioBuffer);
                return audioBuffer;
            } catch (error) {
                console.error(`Failed to load audio: ${url}`, error);
                throw error;
            } finally {
                this.loading.delete(url);
            }
        })();

        this.loading.set(url, promise);
        return promise;
    }

    play(buffer: AudioBuffer, options: { pitch?: number; gain?: number } = {}) {
        if (this.context.state === 'suspended') {
            this.context.resume();
        }

        const source = this.context.createBufferSource();
        const gainNode = this.context.createGain();

        source.buffer = buffer;

        // Humanization: Randomize pitch and volume slightly
        const detune = (options.pitch || 1) + (Math.random() * 0.1 - 0.05);
        source.playbackRate.value = detune;

        const volume = (options.gain || 0.4) + (Math.random() * 0.1 - 0.05);
        gainNode.gain.value = volume;

        source.connect(gainNode);
        gainNode.connect(this.context.destination);

        source.start(0);
    }
}

const engine = typeof window !== 'undefined' ? new AudioEngine() : null;

export function useTypewriterAudio() {
    const { settings } = useAppStore();
    const soundProfile = settings.soundProfile || 'mechanical';

    // Pre-load current profile
    useEffect(() => {
        if (!engine || !settings.typewriterSoundsEnabled) return;

        const types: SoundType[] = ['key', 'space', 'enter', 'delete'];
        if (soundProfile === 'typewriter') types.push('ding');

        types.forEach(type => engine.loadSample(soundProfile, type));
    }, [soundProfile, settings.typewriterSoundsEnabled]);

    const playSound = useCallback(async (type: SoundType = 'key') => {
        if (!settings.typewriterSoundsEnabled || !engine) return;

        try {
            const buffer = await engine.loadSample(soundProfile, type);
            if (buffer) {
                engine.play(buffer, {
                    gain: type === 'ding' ? 0.2 : 0.4
                });
            }
        } catch (e) {
            // Fallback to silent or simple beep if needed
        }
    }, [settings.typewriterSoundsEnabled, soundProfile]);

    return { playSound };
}
